import * as React from 'react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button, FormHelperText, Stack } from '@mui/material'

import { CheckCircle, PlayCircleFilled } from '@mui/icons-material'
import { getCodeSetLabelLookup } from '../../../Functions/CodeSetFns'
import { Box } from '@mui/system'
import { ReadOnlyTextField } from '../../../garvan-react/Components/ReadOnlyTextField'

import { useUserContext } from '../../../Contexts/UserConfig'
import { DataGridPro } from '@mui/x-data-grid-pro'
import PageSection from '../../../Constants&Theme/PageSection'
import ScreenCollectionDetails from './ScreenCollectionDetails'
import {
  useGetScreenCollection,
  useGetScreenCollectionSampleRuns,
  useUpdateScreenCollectionStatus,
} from '../../../APIs/Hooks/ScreenCollectionHooks'
import { useNewSampleRun } from '../../../APIs/Hooks/SampleRunHooks'
import { ScreenCollectionCols } from './ScreenCollectionCols'
import CompleteCollectionDialog from './CompleteCollectionDialog'
import { stackGap } from '../../../Constants&Theme/Spacing'

function ScreenCollection() {
  const navigate = useNavigate()
  const { loginConfig } = useUserContext()
  const [openComplete, setOpenComplete] = useState(false)
  const { collection_id } = useParams()

  const collection = useGetScreenCollection(collection_id).data
  const sampleRunQuery = useGetScreenCollectionSampleRuns(collection_id)
  const { mutate: newRun } = useNewSampleRun()
  const { mutate: updateStatus } =
    useUpdateScreenCollectionStatus(collection_id)

  return (
    <Box
      sx={{
        maxWidth: 'md',
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <PageSection
        title={`Collection: ${collection?.Collection?.ScreenCollectionID}`}
      >
        <Stack
          gap={stackGap.col}
          width={1}
        >
          <Stack
            gap={stackGap.row}
            width={1}
            direction={'row'}
          >
            <ReadOnlyTextField
              label="Request Name"
              value={collection?.Screen?.Name || ''}
              fullWidth
              sx={{ flex: 2 }}
            />
            <ReadOnlyTextField
              label="Holding"
              value={getCodeSetLabelLookup(
                loginConfig,
                ['KV-Holdings'],
                collection?.Screen?.HoldingID,
              )}
              fullWidth
              sx={{ flex: 1 }}
            />
          </Stack>
          <ScreenCollectionDetails collection={collection?.Collection ?? {}} />
        </Stack>
        <Stack
          direction={'row'}
          gap={stackGap.row}
          sx={{ my: 2 }}
        >
          <Box>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<PlayCircleFilled />}
              disabled={collection?.Collection?.CollectionStatus === 'CPT'}
              onClick={() =>
                newRun(
                  {
                    holding_id: collection.Screen.HoldingID,
                    collection_id: collection_id,
                    run_type: collection.Collection.SampleType,
                    destination: collection.Collection.DestinationFacilityID,
                    vessel_type: 'BAG',
                  },
                  {
                    onSuccess: () => {
                      if (collection?.Collection.CollectionStatus !== 'INP') {
                        updateStatus('INP')
                      }
                    },
                  },
                )
              }
            >
              Start New Sample Run
            </Button>
            <FormHelperText>
              Updates status and enables sample collection
            </FormHelperText>
          </Box>
          <Box>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<CheckCircle />}
              disabled={collection?.Collection?.CollectionStatus !== 'INP'}
              onClick={() => setOpenComplete(true)}
            >
              Complete Collection
            </Button>
            <FormHelperText>
              Updates status and Locks editable fields
            </FormHelperText>
          </Box>
        </Stack>
        <PageSection title={'Related Sample Runs'}>
          <DataGridPro
            sx={{ width: 1 }}
            hideFooter
            autoHeight
            loading={sampleRunQuery.isLoading}
            rows={sampleRunQuery.data ?? []}
            getRowId={(row) => row.SampleRunID}
            onRowClick={({ row }) =>
              navigate('/stuart/sample/run/' + row.SampleRunID)
            }
            columns={ScreenCollectionCols(loginConfig)}
          />
        </PageSection>
        <CompleteCollectionDialog
          open={openComplete}
          setOpen={setOpenComplete}
        />
      </PageSection>
    </Box>
  )
}

export default ScreenCollection
